// Wires `buildPullSchemaSnapshot` from the resolver service-layer to the
// production per-engine schema-cache `ensure*` functions. Used by
// `useVqeEditWiring` so a per-tab `SqlResolverService` can refresh its
// `SchemaSnapshot` against a live connection's schema cache.
//
// Each `ensure*Schema` function is idempotent: it returns a cached snapshot
// when present and warms the cache otherwise. Calling them from VQE-Edit does
// not duplicate work the autocomplete schema bus is already doing — both
// paths share the same per-engine cache module.

import { ensureMssqlTablesWithColumns } from '../../../connections/services/language-services/mssqlTableCache';
import { ensureMysqlSchemaWithColumns } from '../../../connections/services/language-services/mysqlSchemaCache';
import { ensureOracleSchemaWithColumns } from '../../../connections/services/language-services/oracleSchemaCache';
import { ensurePgSchemaWithColumns } from '../../../connections/services/language-services/pgSchemaCache';
import { ensureSqliteSchemaWithColumns } from '../../../connections/services/language-services/sqliteSchemaCache';
import type { SqlEngine } from '../../../sql-grammar/model';
import {
  buildPullSchemaSnapshot,
  type PullSchemaSnapshotFn,
} from '../../../sql-resolver/service';
import { fetchMetaInfo } from '../../common/services/metaInfoApi';
import { getRecordedConnectionEngine, isForeignEngine } from '../../../connections/utils/connectionLookup';
import { sqlEngineToDatabaseEngineId } from '../../../../core/engines/sqlEngineToDatabaseEngineId';

/**
 * May a visual-query surface pull `engine`'s schema for `connectionId`? Every
 * VQE surface — compact popover, full tab, DDL editors, dashboard starter —
 * pulls through here, and the check runs on every pull (not when the puller
 * is built), so it holds however late the connection's engine becomes known.
 *
 * The same wrong-engine rule as `connectionEngineGuard.ts`. Its main case is
 * Kusto: a Kusto tab has no SQL dialect, so the visual query editor parses it
 * as 'postgres' to classify the statement under the caret — and that parse
 * default used to pick the schema loader too, sending PostgreSQL catalog
 * queries to Kusto clusters. Not reported as a mismatch: the compact popover
 * sits mounted on every Kusto tab, so this is the expected path, not drift.
 */
function canPullLiveSchema(connectionId: string, engine: SqlEngine): boolean {
  return !isForeignEngine(getRecordedConnectionEngine(connectionId), sqlEngineToDatabaseEngineId(engine));
}

export function buildLivePullSchemaSnapshot(
  connectionId: string,
  engine: SqlEngine,
): PullSchemaSnapshotFn {
  const pull = buildLivePull(connectionId, engine);
  return async (database) => (canPullLiveSchema(connectionId, engine) ? pull(database) : undefined);
}

function buildLivePull(connectionId: string, engine: SqlEngine): PullSchemaSnapshotFn {
  return buildPullSchemaSnapshot(connectionId, engine, {
    // VQE-Edit projects COLUMNS, so the relations-only phase-1 publish the
    // plain `ensure…` resolves on is not enough (SLICES.md § S7 items 1 + 8) —
    // but neither is waiting for ALL of phase 2 needed: FK edges, CHECK
    // clauses, indexes, routines and sequences only feed later suggestions.
    // The `…WithColumns` variants resolve once the column step is in (same
    // policy on every engine; SQLite's one PRAGMA walk makes that the whole
    // phase 2). The rest arrives through the schema bus: `useVqeEditWiring`
    // re-pulls on every `updated` event.
    ensurePgSchema: ensurePgSchemaWithColumns,
    ensureMysqlSchema: ensureMysqlSchemaWithColumns,
    ensureOracleSchema: ensureOracleSchemaWithColumns,
    ensureSqliteSchema: ensureSqliteSchemaWithColumns,
    ensureMssqlTables: ensureMssqlTablesWithColumns,
    fetchLooseJsonDeclarations: async ({ connectionId: c, database: d }) => {
      try {
        const metaInfo = await fetchMetaInfo(c, d);
        return metaInfo?.jsonColumns ?? [];
      } catch {
        // Web mode (no IPC) — silently skip MetaInfo enrichment.
        return [];
      }
    },
    fetchLooseRelationships: async ({ connectionId: c, database: d }) => {
      try {
        const metaInfo = await fetchMetaInfo(c, d);
        return metaInfo?.looseRelationships ?? [];
      } catch {
        // Web mode (no IPC) — silently skip MetaInfo enrichment.
        return [];
      }
    },
  });
}

/**
 * Both engine-id mappings are re-exported from the shared `core/engines`
 * single source of truth so existing consumers keep this import path while
 * the mappings live in exhaustive, dep-free leaf utils (also reused by the
 * Table Designer JSON toggle and the dashboards cross-filter service — the
 * latter runs in the electron main process, which must not pull this
 * schema-cache-heavy module into its bundle).
 */
export { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
export { sqlEngineToDatabaseEngineId } from '../../../../core/engines/sqlEngineToDatabaseEngineId';
